import { useState, useMemo } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { useFizbo } from '@/hooks/useFizbo';
import { useMessages } from '@/hooks/useMessages';
import { usePortfolios } from '@/hooks/usePortfolios';

// ─── Types ───
interface Recipient {
  id: string;
  name: string;
  phone: string;
  varOverrides?: Record<string, string>;
}

// ─── Templates ───
const templates = [
  {
    id: 'bayram',
    name: 'Bayram Mesajı',
    icon: 'ri-moon-line',
    content:
      'Sayın [isim], bayramınız kutlu olsun! Size ve ailenize sağlık, mutluluk ve huzur dileriz. Fırat Bilgin Continental Gayrimenkul',
  },
  {
    id: 'yilbasi',
    name: 'Yeni Yıl Mesajı',
    icon: 'ri-gift-line',
    content:
      'Sayın [isim], yeni yılınız kutlu olsun! Yatırımlarınızın değerlenmesini dileriz. Mutlu yıllar!',
  },
  {
    id: 'portfoy',
    name: 'Portföy Bilgilendirme',
    icon: 'ri-building-line',
    content:
      'Sayın [isim], [lokasyon] lokasyonundaki portföyünüz hakkında güncel bilgi vermek istiyoruz. Detaylar için arayabilirsiniz.',
  },
  {
    id: 'fizbotakip',
    name: 'FİZBO Takip',
    icon: 'ri-map-pin-line',
    content:
      'Sayın [isim], [lokasyon] adresindeki ilanınız hakkında bilgi almak istiyoruz. Sizi arayabilir miyiz?',
  },
  {
    id: 'tesekkur',
    name: 'Teşekkür',
    icon: 'ri-heart-line',
    content:
      'Sayın [isim], bizimle çalıştığınız için teşekkür ederiz. Size en iyi hizmeti sunmaya devam edeceğiz.',
  },
  {
    id: 'bos',
    name: 'Boş Şablon',
    icon: 'ri-edit-line',
    content: '',
  },
];

const variables = ['[isim]', '[lokasyon]', '[fiyat]', '[mülk-türü]'];

// ─── Helpers ───
function formatPhoneForWhatsApp(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits.startsWith('0')) return '9' + digits;
  if (digits.startsWith('5')) return '90' + digits;
  return digits;
}

function personalize(text: string, recipient: Recipient, varValues: Record<string, string> = {}): string {
  return text
    .replace(/\[isim\]/gi, recipient.name)
    .replace(/\[lokasyon\]/gi, recipient.varOverrides?.['[lokasyon]'] || varValues['[lokasyon]'] || '[Lokasyon belirtilmedi]')
    .replace(/\[fiyat\]/gi, recipient.varOverrides?.['[fiyat]'] || varValues['[fiyat]'] || '[Fiyat belirtilmedi]')
    .replace(/\[mülk-türü\]/gi, recipient.varOverrides?.['[mülk-türü]'] || varValues['[mülk-türü]'] || '[Mülk türü belirtilmedi]');
}

// ─── Component ───
export default function TopluMesajPage() {
  const { isDark } = useTheme();
  const { listings: fizboListings, isLoading: fizboLoading } = useFizbo();
  const { messages, loading: historyLoading, addMessage, deleteMessage } = useMessages();
  const { portfolios } = usePortfolios();

  // Editor state
  const [message, setMessage] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [recipients, setRecipients] = useState<Recipient[]>([]);

  // Variable values — user fills these in
  const [varValues, setVarValues] = useState<Record<string, string>>({});
  const [activeVarEditor, setActiveVarEditor] = useState<string | null>(null);

  // Modals
  const [showRecipientPicker, setShowRecipientPicker] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [sentIds, setSentIds] = useState<Set<string>>(new Set());

  // Recipient picker state
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerType, setPickerType] = useState('Tümü');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const fizboTypes = useMemo(() => {
    const set = new Set(fizboListings.map((l) => l.propertyType).filter(Boolean));
    return ['Tümü', ...Array.from(set)];
  }, [fizboListings]);

  const filteredFizbo = useMemo(() => {
    return fizboListings.filter((l) => {
      const matchSearch =
        pickerSearch === '' ||
        l.ownerName.toLowerCase().includes(pickerSearch.toLowerCase()) ||
        l.phone.includes(pickerSearch) ||
        l.location.toLowerCase().includes(pickerSearch.toLowerCase()) ||
        l.listingNo.toLowerCase().includes(pickerSearch.toLowerCase());
      const matchType = pickerType === 'Tümü' || l.propertyType === pickerType;
      return matchSearch && matchType;
    });
  }, [fizboListings, pickerSearch, pickerType]);

  const usedVars = useMemo(() => {
    return variables.filter((v) => message.includes(v));
  }, [message]);

  const portfolioSuggestions = useMemo(() => {
    return portfolios.map((p) => ({
      id: p.id,
      label: `${p.title} — ${p.location} — ${p.price.toLocaleString('tr-TR')} TL`,
      location: p.location,
      price: `${p.price.toLocaleString('tr-TR')} TL`,
      propertyType: p.propertyType,
    }));
  }, [portfolios]);

  const toggleFizbo = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === filteredFizbo.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredFizbo.map((l) => l.id)));
    }
  };

  const confirmRecipients = () => {
    const selected = fizboListings
      .filter((l) => selectedIds.has(l.id))
      .map((l) => ({
        id: l.id,
        name: l.ownerName,
        phone: l.phone,
        varOverrides: {
          '[lokasyon]': l.location,
          '[fiyat]': l.price ? `${l.price.toLocaleString('tr-TR')} TL` : '',
          '[mülk-türü]': l.propertyType,
        },
      }));
    setRecipients(selected);
    setShowRecipientPicker(false);
    setSelectedIds(new Set());
    setPickerSearch('');
    setPickerType('Tümü');
  };

  const handleTemplateSelect = (template: (typeof templates)[0]) => {
    setMessage(template.content);
    setSelectedTemplate(template.id);
  };

  const insertVariable = (variable: string) => {
    const textarea = document.getElementById('msg-textarea') as HTMLTextAreaElement;
    if (!textarea) {
      setMessage((prev) => prev + ' ' + variable);
      return;
    }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const before = message.slice(0, start);
    const after = message.slice(end);
    const newText = before + variable + after;
    setMessage(newText);
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + variable.length, start + variable.length);
    }, 0);
  };

  const removeRecipient = (id: string) => {
    setRecipients((prev) => prev.filter((r) => r.id !== id));
  };

  const setRecipientVar = (recipientId: string, varName: string, value: string) => {
    setRecipients((prev) =>
      prev.map((r) =>
        r.id === recipientId
          ? { ...r, varOverrides: { ...(r.varOverrides || {}), [varName]: value } }
          : r
      )
    );
  };

  const handleSaveDraft = async () => {
    if (!message.trim()) return;
    const foundVars = variables.filter((v) => message.includes(v));
    const rec = await addMessage(
      selectedTemplate
        ? templates.find((t) => t.id === selectedTemplate)?.name || null
        : null,
      message,
      foundVars
    );
    if (rec) {
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    }
  };

  const openWhatsAppForRecipient = (recipient: Recipient) => {
    const personalized = encodeURIComponent(personalize(message, recipient, varValues));
    const phone = formatPhoneForWhatsApp(recipient.phone);
    window.open(`https://wa.me/${phone}?text=${personalized}`, '_blank');
    setSentIds((prev) => new Set(prev).add(recipient.id));
  };

  const handleStartSend = () => {
    if (!message.trim() || recipients.length === 0) return;
    setSentIds(new Set());
    setShowSendModal(true);
  };

  const handleFinishSend = async () => {
    const foundVars = variables.filter((v) => message.includes(v));
    await addMessage(
      selectedTemplate
        ? templates.find((t) => t.id === selectedTemplate)?.name || null
        : null,
      message,
      foundVars
    );
    setShowSendModal(false);
    setMessage('');
    setSelectedTemplate(null);
    setRecipients([]);
    setVarValues({});
    setSentIds(new Set());
  };

  // ─── Styles helpers ───
  const cardBg = isDark ? 'bg-dark-card border-dark-border' : 'bg-white border-light-border';
  const textMain = isDark ? 'text-dark-text' : 'text-light-text';
  const textMuted = isDark ? 'text-dark-muted' : 'text-light-muted';
  const inputBg = isDark ? 'bg-dark-bg border-dark-border text-dark-text placeholder:text-dark-muted' : 'bg-light-bg border-light-border text-light-text placeholder:text-light-muted';

  return (
    <div className="space-y-4 pb-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-green-500">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-message-3-line text-white text-lg" />
            </div>
          </div>
          <div>
            <h1 className={`text-lg font-semibold ${textMain}`}>
              Toplu Mesaj Paneli
            </h1>
            <p className={`text-xs ${textMuted}`}>
              FİZBO kayıtlarından alıcı seç, mesajı kişiselleştir, WhatsApp&apos;a gönder
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: Templates + Variables + Variable Values */}
        <div className="space-y-4">
          {/* Templates */}
          <div className={`rounded-xl p-4 border ${cardBg}`}>
            <h2 className={`text-sm font-semibold mb-3 ${textMain}`}>
              Hazır Şablonlar
            </h2>
            <div className="space-y-2">
              {templates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => handleTemplateSelect(template)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                    selectedTemplate === template.id
                      ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                      : isDark
                      ? 'hover:bg-dark-border'
                      : 'hover:bg-light-bg'
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      selectedTemplate === template.id
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-600'
                        : isDark
                        ? 'bg-dark-bg text-dark-muted'
                        : 'bg-light-bg text-light-muted'
                    }`}
                  >
                    <i className={`${template.icon}`} />
                  </div>
                  <span className={`text-sm font-medium ${textMain}`}>
                    {template.name}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Variables */}
          <div className={`rounded-xl p-4 border ${cardBg}`}>
            <h2 className={`text-sm font-semibold mb-3 ${textMain}`}>
              Değişken Ekle
            </h2>
            <p className={`text-xs ${textMuted} mb-3`}>
              Tıklayarak mesaja ekleyin, aşağıda değerlerini tanımlayın
            </p>
            <div className="flex flex-wrap gap-2">
              {variables.map((variable) => (
                <button
                  key={variable}
                  onClick={() => insertVariable(variable)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                    isDark
                      ? 'border-dark-border text-dark-muted hover:border-green-500 hover:text-green-500'
                      : 'border-light-border text-light-muted hover:border-green-600 hover:text-green-600'
                  }`}
                >
                  {variable}
                </button>
              ))}
            </div>
          </div>

          {/* Variable Values Editor */}
          {usedVars.length > 0 && (
            <div className={`rounded-xl p-4 border ${cardBg}`}>
              <div className="flex items-center justify-between mb-3">
                <h2 className={`text-sm font-semibold ${textMain}`}>
                  Değişken Değerleri
                </h2>
                <span className={`text-xs ${textMuted}`}>
                  Mesajdaki her değişkenin karşılığı
                </span>
              </div>
              <p className={`text-xs ${textMuted} mb-3`}>
                Alıcıya özel farklılık varsa mesaj editörü altından düzenleyin. Burası tüm alıcılar için ortak değer.
              </p>
              <div className="space-y-2.5">
                {usedVars.map((v) => (
                  <div key={v} className="flex items-center gap-2">
                    <span className={`text-xs font-medium w-20 flex-shrink-0 ${textMain}`}>
                      {v}
                    </span>
                    <input
                      type="text"
                      value={varValues[v] || ''}
                      onChange={(e) =>
                        setVarValues((prev) => ({ ...prev, [v]: e.target.value }))
                      }
                      placeholder={`${v.slice(1, -1)} girin...`}
                      className={`flex-1 text-sm px-3 py-2 rounded-lg border outline-none transition-colors ${inputBg} focus:border-green-500`}
                    />
                  </div>
                ))}
              </div>
              {portfolioSuggestions.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
                  <p className={`text-xs ${textMuted} mb-2`}>
                    Portföyden hızlı doldur:
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {portfolioSuggestions.slice(0, 3).map((p) => (
                      <button
                        key={p.id}
                        onClick={() => {
                          setVarValues((prev) => ({
                            ...prev,
                            '[lokasyon]': p.location,
                            '[fiyat]': p.price,
                            '[mülk-türü]': p.propertyType,
                          }));
                        }}
                        className={`text-[10px] px-2 py-1 rounded-md border transition-colors ${
                          isDark
                            ? 'border-dark-border text-dark-muted hover:border-green-500'
                            : 'border-light-border text-light-muted hover:border-green-600'
                        }`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right: Editor */}
        <div className="lg:col-span-2 space-y-4">
          {/* Recipients */}
          <div className={`rounded-xl p-4 border ${cardBg}`}>
            <div className="flex items-center justify-between mb-2">
              <label className={`text-sm font-medium ${textMain}`}>
                Alıcılar
              </label>
              <button
                onClick={() => setShowRecipientPicker(true)}
                className="text-xs px-3 py-1.5 rounded-lg bg-green-500 hover:bg-green-600 text-white font-medium transition-colors flex items-center gap-1.5 whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-user-add-line" />
                </div>
                FİZBO&apos;dan Seç
              </button>
            </div>

            {recipients.length === 0 ? (
              <div
                onClick={() => setShowRecipientPicker(true)}
                className={`cursor-pointer border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  isDark
                    ? 'border-dark-border hover:border-green-500/50'
                    : 'border-light-border hover:border-green-400'
                }`}
              >
                <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-green-50 dark:bg-green-900/20">
                  <i className="ri-contacts-book-2-line text-green-500 text-lg" />
                </div>
                <p className={`text-sm font-medium ${textMain}`}>
                  Henüz alıcı seçilmedi
                </p>
                <p className={`text-xs ${textMuted} mt-1`}>
                  Tıklayarak FİZBO kayıtlarından alıcı seçin
                </p>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {recipients.map((recipient) => (
                  <div
                    key={recipient.id}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs border ${
                      isDark
                        ? 'bg-green-900/20 border-green-800 text-green-400'
                        : 'bg-green-50 border-green-200 text-green-700'
                    }`}
                  >
                    <span className="font-medium">{recipient.name}</span>
                    <span className={textMuted}>{recipient.phone}</span>
                    <button
                      onClick={() => removeRecipient(recipient.id)}
                      className="w-4 h-4 flex items-center justify-center hover:text-red-500 transition-colors"
                    >
                      <i className="ri-close-line" />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => setShowRecipientPicker(true)}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs border transition-colors ${
                    isDark
                      ? 'border-dark-border text-dark-muted hover:border-green-500 hover:text-green-500'
                      : 'border-light-border text-light-muted hover:border-green-600 hover:text-green-600'
                  }`}
                >
                  <i className="ri-add-line" />
                  Ekle
                </button>
              </div>
            )}
            {recipients.length > 0 && (
              <p className={`text-xs mt-2 ${textMuted}`}>
                {recipients.length} alıcı seçildi
              </p>
            )}
          </div>

          {/* Message Editor */}
          <div className={`rounded-xl p-4 border ${cardBg}`}>
            <label className={`block text-sm font-medium mb-2 ${textMain}`}>
              Mesaj İçeriği
            </label>
            <textarea
              id="msg-textarea"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Mesajınızı yazın... [isim], [lokasyon] gibi değişkenler kullanarak kişiselleştirin. Gönderimde otomatik olarak her alıcının bilgisiyle doldurulur."
              rows={8}
              className={`w-full px-3 py-2 rounded-lg text-sm border outline-none resize-none transition-colors ${inputBg} focus:border-green-500`}
            />
            <div className="flex items-center justify-between mt-2">
              <p className={`text-xs ${textMuted}`}>
                {message.length} karakter
              </p>
              <p className={`text-xs ${textMuted}`}>
                WhatsApp limiti: 4096
              </p>
            </div>
          </div>

          {/* Per-Recipient Variable Editor */}
          {recipients.length > 0 && usedVars.length > 1 && (
            <div className={`rounded-xl p-4 border ${cardBg}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className={`text-sm font-semibold ${textMain}`}>
                  Alıcıya Özel Değişkenler
                </h3>
                <span className={`text-xs ${textMuted}`}>
                  Her alıcı için farklı değerler girebilirsiniz
                </span>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {recipients.map((recipient) => (
                  <div
                    key={recipient.id}
                    className={`rounded-lg p-3 border ${
                      isDark ? 'bg-dark-bg border-dark-border' : 'bg-gray-50 border-gray-100'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <p className={`text-xs font-semibold ${textMain}`}>
                        {recipient.name}
                      </p>
                      <button
                        onClick={() =>
                          setActiveVarEditor(activeVarEditor === recipient.id ? null : recipient.id)
                        }
                        className="text-xs text-green-600 hover:underline"
                      >
                        {activeVarEditor === recipient.id ? 'Kapat' : 'Düzenle'}
                      </button>
                    </div>
                    {activeVarEditor === recipient.id && (
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        {usedVars
                          .filter((v) => v !== '[isim]')
                          .map((v) => (
                            <div key={v}>
                              <label className={`text-[10px] ${textMuted} block mb-1`}>
                                {v}
                              </label>
                              <input
                                type="text"
                                value={recipient.varOverrides?.[v] || varValues[v] || ''}
                                onChange={(e) =>
                                  setRecipientVar(recipient.id, v, e.target.value)
                                }
                                placeholder={`${v.slice(1, -1)}...`}
                                className={`w-full text-xs px-2 py-1.5 rounded-md border outline-none transition-colors ${inputBg} focus:border-green-500`}
                              />
                            </div>
                          ))}
                      </div>
                    )}
                    {activeVarEditor !== recipient.id && (
                      <div className="flex flex-wrap gap-1">
                        {usedVars
                          .filter((v) => v !== '[isim]')
                          .map((v) => (
                            <span
                              key={v}
                              className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                                recipient.varOverrides?.[v] || varValues[v]
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                  : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                              }`}
                            >
                              {v}: {recipient.varOverrides?.[v] || varValues[v] || '—'}
                            </span>
                          ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Live Preview */}
          {recipients.length > 0 && message.includes('[isim]') && (
            <div className={`rounded-xl p-4 border ${cardBg}`}>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-eye-line ${textMuted}`} />
                </div>
                <h3 className={`text-sm font-semibold ${textMain}`}>
                  Alıcı Önizlemesi
                </h3>
                <span className={`text-xs ${textMuted}`}>
                  (değişkenler otomatik doldurulur)
                </span>
              </div>
              <div className="space-y-2">
                {recipients.slice(0, 3).map((recipient) => (
                  <div
                    key={recipient.id}
                    className={`rounded-lg p-3 border ${
                      isDark ? 'bg-dark-bg border-dark-border' : 'bg-gray-50 border-gray-100'
                    }`}
                  >
                    <p className={`text-xs font-semibold mb-1 ${textMain}`}>
                      {recipient.name}:
                    </p>
                    <p className={`text-xs ${textMuted} leading-relaxed`}>
                      {personalize(message, recipient, varValues)}
                    </p>
                  </div>
                ))}
                {recipients.length > 3 && (
                  <p className={`text-xs text-center ${textMuted}`}>
                    + {recipients.length - 3} alıcı daha...
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleStartSend}
              disabled={!message.trim() || recipients.length === 0}
              className="flex-1 py-3 px-4 bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white font-medium text-sm rounded-lg transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-whatsapp-line text-lg" />
              </div>
              WhatsApp&apos;a Gönder
            </button>
            <button
              onClick={handleSaveDraft}
              disabled={!message.trim()}
              className="flex-1 py-3 px-4 font-medium text-sm rounded-lg border transition-colors disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap"
              style={{
                borderColor: isDark ? '#374151' : '#e5e7eb',
                color: isDark ? '#9ca3af' : '#6b7280',
              }}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-save-line" />
              </div>
              {saved ? 'Kaydedildi!' : 'Taslak Kaydet'}
            </button>
          </div>
        </div>
      </div>

      {/* ─── Gönderim Geçmişi ─── */}
      <div className={`rounded-xl border ${cardBg} overflow-hidden`}>
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <h2 className={`text-sm font-semibold ${textMain} flex items-center gap-2`}>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-history-line" />
            </div>
            Gönderim Geçmişi
          </h2>
        </div>
        {historyLoading ? (
          <div className="p-6 text-center">
            <div className="w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
            <p className={`text-xs ${textMuted}`}>Yükleniyor...</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="p-6 text-center">
            <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
              <i className="ri-draft-line text-gray-400 text-lg" />
            </div>
            <p className={`text-sm font-medium ${textMain}`}>
              Henüz kaydedilmiş mesaj yok
            </p>
            <p className={`text-xs ${textMuted} mt-1`}>
              Taslak kaydederek buraya ekleyebilirsiniz
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {messages.map((msg) => (
              <div key={msg.id} className="px-4 py-3 flex items-start gap-3 group">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-green-50 dark:bg-green-900/20 flex-shrink-0">
                  <i className="ri-message-3-line text-green-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-sm font-medium ${textMain}`}>
                      {msg.template_name || 'Özel Mesaj'}
                    </span>
                    <span className={`text-xs ${textMuted}`}>
                      {new Date(msg.created_at).toLocaleDateString('tr-TR')}
                    </span>
                  </div>
                  <p className={`text-xs ${textMuted} truncate`}>
                    {msg.content}
                  </p>
                  {msg.variables.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {msg.variables.map((v) => (
                        <span
                          key={v}
                          className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400"
                        >
                          {v}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => {
                    setMessage(msg.content);
                    setSelectedTemplate(
                      templates.find((t) => t.name === msg.template_name)?.id || null
                    );
                  }}
                  className="text-xs px-2 py-1 rounded-md text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors opacity-0 group-hover:opacity-100 whitespace-nowrap"
                >
                  Yeniden Kullan
                </button>
                <button
                  onClick={() => deleteMessage(msg.id)}
                  className="w-7 h-7 flex items-center justify-center rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors opacity-0 group-hover:opacity-100"
                >
                  <i className="ri-delete-bin-line" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ─── WhatsApp Send Modal ─── */}
      {showSendModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setShowSendModal(false)}
          />
          <div
            className={`relative w-full max-w-md max-h-[85vh] rounded-xl border overflow-hidden flex flex-col ${
              isDark ? 'bg-dark-card border-dark-border' : 'bg-white border-light-border'
            }`}
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div>
                <h3 className={`text-sm font-semibold ${textMain}`}>
                  WhatsApp Gönderimi
                </h3>
                <p className={`text-xs ${textMuted}`}>
                  Her alıcıya tıklayarak gönderin ({sentIds.size}/{recipients.length})
                </p>
              </div>
              <button
                onClick={() => setShowSendModal(false)}
                className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${
                  isDark ? 'hover:bg-dark-border text-dark-muted' : 'hover:bg-light-bg text-light-muted'
                }`}
              >
                <i className="ri-close-line" />
              </button>
            </div>

            {/* Recipient List */}
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {recipients.map((recipient) => {
                const isSent = sentIds.has(recipient.id);
                return (
                  <button
                    key={recipient.id}
                    onClick={() => openWhatsAppForRecipient(recipient)}
                    disabled={isSent}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
                      isSent
                        ? isDark
                          ? 'bg-green-900/10 border-green-900/30 opacity-60'
                          : 'bg-green-50 border-green-200 opacity-60'
                        : isDark
                        ? 'bg-dark-bg border-dark-border hover:border-green-500'
                        : 'bg-gray-50 border-gray-100 hover:border-green-400'
                    }`}
                  >
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isSent ? 'bg-gray-400' : 'bg-green-500'
                      }`}
                    >
                      {isSent ? (
                        <i className="ri-check-line text-white text-lg" />
                      ) : (
                        <i className="ri-whatsapp-line text-white text-lg" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${textMain}`}>
                        {recipient.name}
                      </p>
                      <p className={`text-xs ${textMuted} truncate`}>
                        {personalize(message, recipient, varValues)}
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      {isSent ? (
                        <span className="text-xs text-green-600 font-medium">Gönderildi</span>
                      ) : (
                        <div className="w-8 h-8 flex items-center justify-center">
                          <i className="ri-external-link-line text-green-500" />
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex gap-2">
              <button
                onClick={() => setShowSendModal(false)}
                className={`py-2.5 px-4 rounded-lg text-sm font-medium border transition-colors ${
                  isDark
                    ? 'border-dark-border text-dark-muted hover:bg-dark-border'
                    : 'border-light-border text-light-muted hover:bg-light-bg'
                }`}
              >
                Kapat
              </button>
              <button
                onClick={handleFinishSend}
                className="flex-1 py-2.5 px-4 rounded-lg text-sm font-medium bg-green-500 hover:bg-green-600 text-white transition-colors flex items-center justify-center gap-2"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-check-line" />
                </div>
                Gönderim Tamamlandı
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Recipient Picker Modal (FİZBO) ─── */}
      {showRecipientPicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setShowRecipientPicker(false)}
          />
          <div
            className={`relative w-full max-w-lg max-h-[80vh] rounded-xl border overflow-hidden flex flex-col ${
              isDark ? 'bg-dark-card border-dark-border' : 'bg-white border-light-border'
            }`}
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className={`text-sm font-semibold ${textMain}`}>
                FİZBO Alıcı Seç ({selectedIds.size})
              </h3>
              <button
                onClick={() => setShowRecipientPicker(false)}
                className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${
                  isDark ? 'hover:bg-dark-border text-dark-muted' : 'hover:bg-light-bg text-light-muted'
                }`}
              >
                <i className="ri-close-line" />
              </button>
            </div>

            {/* Filters */}
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-search-line ${textMuted}`} />
                </div>
                <input
                  value={pickerSearch}
                  onChange={(e) => setPickerSearch(e.target.value)}
                  placeholder="İsim, telefon, lokasyon veya ilan no ara..."
                  className={`flex-1 text-sm bg-transparent outline-none ${textMain} placeholder:text-gray-400`}
                />
              </div>
              <div className="flex flex-wrap gap-1.5">
                {fizboTypes.map((type) => (
                  <button
                    key={type}
                    onClick={() => setPickerType(type)}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors whitespace-nowrap ${
                      pickerType === type
                        ? 'bg-green-500 text-white border-green-500'
                        : isDark
                        ? 'border-dark-border text-dark-muted hover:border-green-500'
                        : 'border-light-border text-light-muted hover:border-green-600'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            {/* FİZBO List */}
            <div className="flex-1 overflow-y-auto">
              {fizboLoading ? (
                <div className="p-6 text-center">
                  <div className="w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                  <p className={`text-xs ${textMuted}`}>Yükleniyor...</p>
                </div>
              ) : filteredFizbo.length === 0 ? (
                <div className="p-6 text-center">
                  <p className={`text-sm ${textMuted}`}>Sonuç bulunamadı</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100 dark:divide-gray-800">
                  {/* Select All */}
                  <button
                    onClick={toggleAll}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                      isDark ? 'hover:bg-dark-border' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                        selectedIds.size === filteredFizbo.length && filteredFizbo.length > 0
                          ? 'bg-green-500 border-green-500'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                    >
                      {selectedIds.size === filteredFizbo.length && filteredFizbo.length > 0 && (
                        <i className="ri-check-line text-white text-xs" />
                      )}
                    </div>
                    <span className={`text-sm font-medium ${textMain}`}>
                      Tümünü Seç ({filteredFizbo.length})
                    </span>
                  </button>

                  {filteredFizbo.map((listing) => (
                    <button
                      key={listing.id}
                      onClick={() => toggleFizbo(listing.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                        isDark ? 'hover:bg-dark-border' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div
                        className={`w-5 h-5 rounded border flex items-center justify-center transition-colors flex-shrink-0 ${
                          selectedIds.has(listing.id)
                            ? 'bg-green-500 border-green-500'
                            : 'border-gray-300 dark:border-gray-600'
                        }`}
                      >
                        {selectedIds.has(listing.id) && (
                          <i className="ri-check-line text-white text-xs" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0 text-left">
                        <p className={`text-sm font-medium ${textMain}`}>
                          {listing.ownerName}
                        </p>
                        <p className={`text-xs ${textMuted}`}>
                          {listing.phone}
                          <span className="mx-1">·</span>
                          {listing.location}
                          <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] bg-gray-100 dark:bg-gray-800">
                            {listing.propertyType}
                          </span>
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex gap-2">
              <button
                onClick={() => setShowRecipientPicker(false)}
                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium border transition-colors ${
                  isDark
                    ? 'border-dark-border text-dark-muted hover:bg-dark-border'
                    : 'border-light-border text-light-muted hover:bg-light-bg'
                }`}
              >
                İptal
              </button>
              <button
                onClick={confirmRecipients}
                disabled={selectedIds.size === 0}
                className="flex-1 py-2.5 px-4 rounded-lg text-sm font-medium bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white transition-colors"
              >
                {selectedIds.size} Kişiyi Ekle
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}